import { useCallback, useEffect, useMemo, useState } from 'react'

import Papa from 'papaparse'
import { useTranslation } from 'react-i18next'
import { load } from 'scrivito'

import { matchingConfig } from './matching/config'
import { matchColumnsAI } from './matching/layer-ai'
import {
  detectHeaderRow,
  mergeMatches,
  shouldEscalateToAi,
} from './matching/layer-fuzzy'
import { logMatchSummary } from './matching/matchingLog'
import {
  FieldCandidate,
  MatchConfidence,
  MatchResult,
  ModelConfidence,
} from './matching/types'
import {
  buildEnumLookups,
  CsvImportContext,
  CsvRowIssueErrorDetails,
  EMAIL_ATTR_KEY,
  FailedImportRow,
  FailedReason,
  getAnalysisBanner,
  ID_ATTR_KEY,
  resolveEnum,
  resolveEnumArray,
} from './utils'
import { Company } from '@/Data/Company/CompanyDataClass'
import { DataClassName } from '@/Data/const'
import { ContactListRelation } from '@/Data/ContactListRelation/ContactListRelationDataClass'
import { Person } from '@/Data/Person/PersonDataClass'
import { SchemaAttributes } from '@/Data/typesV2'

export const CSV_STEP_COUNT = 4
export const CSV_MAX_ROWS = 20_000
const BATCH_SIZE = 10
const BATCH_DELAY_MS = 100

export type ImportStatus = 'idle' | 'running' | 'done'

export interface CsvColumn {
  rawIndex: number
  header: string
  exampleValue: string
}

export interface CsvRowIssue {
  rowIndex: number
  values: string[]
  issues: Array<'malformed' | 'duplicate'>
  errorDetails: CsvRowIssueErrorDetails
}

export interface CsvAnalysis {
  totalRows: number
  validCount: number
  malformedCount: number
  duplicateCount: number
  duplicateFirstCount: number
  issues: CsvRowIssue[]
}

interface UseCsvModalOptions {
  context: CsvImportContext
  listId?: string
  entityName: DataClassName.COMPANY | DataClassName.PERSON
  onClose: () => void
  mandatoryAttributes: Set<string>
  schemaAttributes: SchemaAttributes | undefined
  fieldCandidates: FieldCandidate[]
  initialFile?: File
  onImportStatusChange?: (status: ImportStatus) => void
}

// Carries a structured, translatable reason from a failed `importRow` through
// `Promise.allSettled` to the batch handler, which stores it for the export.
class RowImportError extends Error {
  constructor(readonly failedReason: FailedReason) {
    super(failedReason.code)
  }
}

export function useCsvModal({
  context,
  listId,
  entityName,
  onClose,
  mandatoryAttributes,
  schemaAttributes,
  fieldCandidates,
  initialFile,
  onImportStatusChange,
}: UseCsvModalOptions) {
  const { t } = useTranslation()
  const importDataClass =
    entityName === DataClassName.COMPANY ? Company : Person

  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [csvRows, setCsvRows] = useState<string[][]>([])
  const [useFirstRowAsHeaders, setUseFirstRowAsHeaders] = useState(true)
  // When on, existing records are matched by their `email` value instead of `_id`.
  const [useEmailAsKey, setUseEmailAsKeyState] = useState(false)
  const [columnMappings, setColumnMappings] = useState<Record<number, string>>(
    {},
  )
  const [columnMatchConfidence, setColumnMatchConfidence] = useState<
    Record<number, MatchConfidence>
  >({})
  // The AI's self-reported per-mapping confidence, shown on hover over the AI
  // chip in Map Fields. Only the AI layer sets it; fuzzy mappings have none.
  const [columnModelConfidence, setColumnModelConfidence] = useState<
    Record<number, ModelConfidence>
  >({})
  const [isMatchingAI, setIsMatchingAI] = useState(false)

  const [importStatus, setImportStatus] = useState<ImportStatus>('idle')
  const [createdCount, setCreatedCount] = useState(0)
  const [updatedCount, setUpdatedCount] = useState(0)
  const [failedCount, setFailedCount] = useState(0)
  const [failedImportRowValues, setFailedImportRowValues] = useState<
    FailedImportRow[]
  >([])
  const [importDuration, setImportDuration] = useState<number | null>(null)

  const enumLookups = useMemo(
    () => buildEnumLookups(schemaAttributes ?? {}),
    [schemaAttributes],
  )

  // Identity fields drive the offline→AI escalation decision: the mandatory,
  // free-text attributes a real source CSV always carries (familyName, name).
  // The mandatory enum `group` is CRM-assigned and never present in an upload,
  // so excluding enums/references/booleans keeps it from forcing every file to
  // the AI. If offline resolves these, the parse is trustworthy and we skip AI.
  const identityFieldKeys = useMemo(
    () =>
      [...mandatoryAttributes].filter((key) => {
        const type = schemaAttributes?.[key]?.type
        return (
          type !== 'enum' &&
          type !== 'enumarray' &&
          type !== 'reference' &&
          type !== 'boolean'
        )
      }),
    [mandatoryAttributes, schemaAttributes],
  )

  const csvColumns = useMemo((): CsvColumn[] => {
    if (csvRows.length === 0) {
      return []
    }
    const headerRow = csvRows[0] ?? []
    const exampleRow = useFirstRowAsHeaders ? csvRows[1] : csvRows[0]
    return headerRow.map((cell, i) => {
      const fallback = t('components.csv.columnFallback', { index: i + 1 })
      return {
        rawIndex: i,
        header: useFirstRowAsHeaders ? cell || fallback : fallback,
        exampleValue: exampleRow?.[i] ?? '',
      }
    })
  }, [csvRows, useFirstRowAsHeaders, t])

  const csvAnalysis = useMemo((): CsvAnalysis => {
    const dataRows = useFirstRowAsHeaders ? csvRows.slice(1) : csvRows
    const mappedIndices = Object.keys(columnMappings)
      .map(Number)
      .filter((i) => columnMappings[i])
    const mandatoryMappedIndices = mappedIndices.filter((i) => {
      const attrKey = columnMappings[i]
      return (
        attrKey !== undefined &&
        attrKey !== ID_ATTR_KEY &&
        mandatoryAttributes.has(attrKey)
      )
    })

    // The active match key uniquely identifies a record, so duplicate detection
    // keys on it alone: email when email-key is on and mapped, else the ID
    // column, else fall back to all mapped columns.
    const idMappingEntry = Object.entries(columnMappings).find(
      ([, v]) => v === ID_ATTR_KEY,
    )
    const idMappedIndex =
      idMappingEntry !== undefined ? Number(idMappingEntry[0]) : null
    const emailMappingEntry = Object.entries(columnMappings).find(
      ([, v]) => v === EMAIL_ATTR_KEY,
    )
    const emailMappedIndex =
      emailMappingEntry !== undefined ? Number(emailMappingEntry[0]) : null
    const duplicateKeyIndices =
      useEmailAsKey && emailMappedIndex !== null
        ? [emailMappedIndex]
        : idMappedIndex !== null
          ? [idMappedIndex]
          : mappedIndices

    if (dataRows.length === 0) {
      return {
        totalRows: 0,
        validCount: 0,
        malformedCount: 0,
        duplicateCount: 0,
        duplicateFirstCount: 0,
        issues: [],
      }
    }

    const issueMap = new Map<number, CsvRowIssue>()
    const seenKeys = new Map<string, number>()
    const duplicateFirstRows = new Set<number>()

    dataRows.forEach((row, i) => {
      const rowIssues: CsvRowIssue['issues'] = []
      const missingFields: string[] = []
      const invalidEnums: CsvRowIssueErrorDetails['invalidEnums'] = []

      for (const idx of mandatoryMappedIndices) {
        const attrKey = columnMappings[idx]
        if (attrKey && !row[idx]?.trim()) missingFields.push(attrKey)
      }

      for (const idx of mappedIndices) {
        const attrKey = columnMappings[idx]
        if (!attrKey || attrKey === ID_ATTR_KEY) continue
        const entry = enumLookups[attrKey]
        if (!entry) continue
        const val = (row[idx] ?? '').trim()
        if (!val) continue
        if (entry.type === 'enumarray') {
          if (resolveEnumArray(entry.map, val) === null) {
            const invalid = val
              .split(',')
              .map((s) => s.trim())
              .filter((s) => !entry.map.has(s.toLowerCase()))
            invalidEnums.push({ attrKey, rawValue: invalid.join(', ') })
          }
        } else if (resolveEnum(entry.map, val) === null) {
          invalidEnums.push({ attrKey, rawValue: val })
        }
      }

      if (missingFields.length > 0 || invalidEnums.length > 0) {
        rowIssues.push('malformed')
      }

      if (duplicateKeyIndices.length > 0) {
        const key = duplicateKeyIndices
          .map((idx) => (row[idx] ?? '').trim().toLowerCase())
          .join('\x00')
        if (seenKeys.has(key)) {
          rowIssues.push('duplicate')
          duplicateFirstRows.add(seenKeys.get(key)!)
        } else {
          seenKeys.set(key, i)
        }
      }

      if (rowIssues.length > 0) {
        issueMap.set(i, {
          rowIndex: i,
          values: row,
          issues: rowIssues,
          errorDetails: { missingFields, invalidEnums },
        })
      }
    })

    const issues = Array.from(issueMap.values())
    return {
      totalRows: dataRows.length,
      validCount: dataRows.length - issueMap.size,
      malformedCount: issues.filter((r) => r.issues.includes('malformed'))
        .length,
      duplicateCount: issues.filter((r) => r.issues.includes('duplicate'))
        .length,
      duplicateFirstCount: duplicateFirstRows.size,
      issues,
    }
  }, [
    csvRows,
    columnMappings,
    useFirstRowAsHeaders,
    useEmailAsKey,
    mandatoryAttributes,
    enumLookups,
  ])

  const stepTitles = useMemo(
    () => [
      t('components.csv.step.selectFile'),
      t('components.csv.step.mapFields'),
      t('components.csv.step.analysis'),
      t('components.csv.step.import'),
    ],
    [t],
  )

  const applyMatches = useCallback((matches: MatchResult[]) => {
    const mappings: Record<number, string> = {}
    const confidence: Record<number, MatchConfidence> = {}
    const modelConfidence: Record<number, ModelConfidence> = {}
    for (const match of matches) {
      if (match.fieldKey) {
        mappings[match.rawIndex] = match.fieldKey
        confidence[match.rawIndex] = match.confidence
        if (match.modelConfidence) {
          modelConfidence[match.rawIndex] = match.modelConfidence
        }
      }
    }
    setColumnMappings(mappings)
    setColumnMatchConfidence(confidence)
    setColumnModelConfidence(modelConfidence)
  }, [])

  const parseCsv = useCallback(
    (
      incoming: File,
      delimiter: string | undefined,
      headerRowIndex: number,
    ): Promise<string[][]> =>
      new Promise((resolve) => {
        Papa.parse<string[]>(incoming, {
          skipEmptyLines: true,
          transform: (value) => value.trim(),
          ...(delimiter ? { delimiter } : {}),
          complete: ({ data }) => {
            // AI may report preamble rows before the header. Drop them so row 0
            // is the header — the rest of the pipeline assumes headerRowIndex 0.
            // Clamp the (untrusted) index into range so a bad value can't slice
            // from the end or discard the whole file.
            const skip =
              headerRowIndex > 0 && headerRowIndex < data.length
                ? headerRowIndex
                : 0
            resolve(skip > 0 ? data.slice(skip) : data)
          },
        })
      }),
    [],
  )

  const runAiMatch = useCallback(
    async (
      incoming: File,
      offline?: { rowIndex: number; matches: MatchResult[] },
    ): Promise<boolean> => {
      const text = await incoming.text()
      const rawLines = text.split('\n').slice(0, matchingConfig.maxAiScanRows)

      setIsMatchingAI(true)
      // matchColumnsAI never throws — any failure resolves to null.
      const aiResult = await matchColumnsAI(rawLines, fieldCandidates)
      setIsMatchingAI(false)

      if (!aiResult) {
        return false
      }
      const rows = await parseCsv(
        incoming,
        aiResult.delimiter,
        aiResult.headerRowIndex,
      )

      // Merge only when the AI parsed the file into the SAME columns as offline
      // (same header row → same indices). Then offline "Match" chips survive and
      // the AI fills only the gaps. If the AI chose a different header row, the
      // indices don't line up, so take the AI result wholesale.
      const alignable =
        matchingConfig.mergeOfflineWithAi &&
        offline !== undefined &&
        offline.rowIndex === aiResult.headerRowIndex
      const mappings = alignable
        ? mergeMatches(offline.matches, aiResult.mappings)
        : aiResult.mappings

      setCsvRows(rows)
      setUseFirstRowAsHeaders(true)
      logMatchSummary(alignable ? 'merged' : 'ai', rows[0] ?? [], mappings)
      applyMatches(mappings)
      return true
    },
    [applyMatches, fieldCandidates, parseCsv],
  )

  const handleFileSelect = useCallback(
    async (incoming: File) => {
      setFile(incoming)
      setColumnMappings({})
      setColumnMatchConfidence({})
      setColumnModelConfidence({})

      const { offlineEnabled, aiEnabled } = matchingConfig

      // Offline-first. papaparse auto-detects the delimiter (the pre-existing
      // modal always relied on this), then detectHeaderRow skips any preamble.
      // The offline matcher resolves the common clean files with no network at
      // all. The AI is called only when the offline result is too weak to trust
      // (identity field unmatched → likely a bad parse or a genuinely hard file).
      // `matchingConfig` gates each layer: offline+ai is the default; either can
      // be turned off via ?csvOffline=0 / ?csvAi=0 for debugging. Both off falls
      // back to the pre-feature behaviour — parse row 0, no auto-mapping.
      if (offlineEnabled) {
        const autoRows = await parseCsv(incoming, undefined, 0)
        const scan = detectHeaderRow(autoRows, fieldCandidates)
        const offlineRows =
          scan.rowIndex > 0 ? autoRows.slice(scan.rowIndex) : autoRows
        const fuzzyMatches = scan.matches

        const applyOffline = () => {
          setCsvRows(offlineRows)
          setUseFirstRowAsHeaders(true)
          logMatchSummary('fuzzy', offlineRows[0] ?? [], fuzzyMatches)
          applyMatches(fuzzyMatches)
        }

        if (
          !aiEnabled ||
          !shouldEscalateToAi(fuzzyMatches, identityFieldKeys)
        ) {
          applyOffline()
          return
        }
        // Escalate to AI, passing the offline result so its matches can be
        // merged when the parses align. On AI failure keep the offline result.
        if (!(await runAiMatch(incoming, scan))) {
          applyOffline()
        }
        return
      }

      // Offline disabled: go straight to AI when enabled, else parse only.
      if (aiEnabled && (await runAiMatch(incoming))) {
        return
      }

      // Pre-feature fallback: parse row 0, no auto-mapping — the user maps
      // every column by hand.
      const rows = await parseCsv(incoming, undefined, 0)
      setCsvRows(rows)
      setUseFirstRowAsHeaders(true)
    },
    [applyMatches, fieldCandidates, identityFieldKeys, parseCsv, runAiMatch],
  )

  // Dev diagnostic, gated behind matchingConfig.batchDiagnosticEnabled. Runs a
  // batch of files through the REAL offline→escalate→AI→merge pipeline WITHOUT
  // touching modal state, and prints a summary + per-column console.table so we
  // can confirm the whole system — including the live AI path the Node sweep
  // can't reach — is behaving. Triggered by the button in SelectFileStep.
  const runBatchDiagnostic = useCallback(
    async (files: File[]) => {
      setIsMatchingAI(true)
      // fieldKey → human label, so the detail table reads against the schema.
      const labelOf = new Map(fieldCandidates.map((c) => [c.key, c.label]))
      const summary: Array<Record<string, unknown>> = []
      // Every column of every file, so wrong/duplicate mappings are visible —
      // a match COUNT hides both (a wrong City→Category and two columns→Email
      // both count as "matched"). The detail rows are the real evaluation.
      const detail: Array<Record<string, unknown>> = []

      for (const file of files) {
        const autoRows = await parseCsv(file, undefined, 0)
        const scan = detectHeaderRow(autoRows, fieldCandidates)
        const escalates = shouldEscalateToAi(scan.matches, identityFieldKeys)

        let source = 'fuzzy'
        let mappings = scan.matches
        let headerCells = autoRows[scan.rowIndex] ?? []

        if (escalates) {
          const text = await file.text()
          const rawLines = text
            .split('\n')
            .slice(0, matchingConfig.maxAiScanRows)
          const ai = await matchColumnsAI(rawLines, fieldCandidates)
          if (ai) {
            const aiRows = await parseCsv(file, ai.delimiter, ai.headerRowIndex)
            const alignable = scan.rowIndex === ai.headerRowIndex
            mappings = alignable
              ? mergeMatches(scan.matches, ai.mappings)
              : ai.mappings
            source = alignable ? 'merged' : 'ai'
            headerCells = aiRows[0] ?? []
          } else {
            source = 'ai-failed→fuzzy'
          }
        }

        const byIndex = new Map(mappings.map((m) => [m.rawIndex, m]))
        // How many columns claim each field — >1 means a duplicate mapping.
        const keyCount = new Map<string, number>()
        for (const m of mappings) {
          if (m.fieldKey) {
            keyCount.set(m.fieldKey, (keyCount.get(m.fieldKey) ?? 0) + 1)
          }
        }
        const matched = mappings.filter((m) => m.fieldKey).length
        const total = headerCells.length

        headerCells.forEach((header, index) => {
          const m = byIndex.get(index)
          const key = m?.fieldKey
          const dup = key ? (keyCount.get(key) ?? 0) > 1 : false
          detail.push({
            file: file.name,
            column: header,
            mappedTo: key ? `${labelOf.get(key) ?? key} (${key})` : '—',
            tier: m?.confidence ?? '—',
            score: m?.score ?? '',
            dup: dup ? 'DUP' : '',
          })
        })

        summary.push({
          file: file.name,
          source,
          matched,
          total,
          rate: total ? `${Math.round((matched / total) * 100)}%` : '—',
          headerRow: scan.rowIndex,
          escalated: escalates,
        })
      }
      setIsMatchingAI(false)
      console.table(summary)
      console.table(detail)
    },
    [fieldCandidates, identityFieldKeys, parseCsv],
  )

  useEffect(() => {
    if (initialFile) {
      void handleFileSelect(initialFile)
    }
  }, [initialFile, handleFileSelect])

  const handleClose = () => {
    setFile(null)
    setStep(0)
    setCsvRows([])
    setUseFirstRowAsHeaders(true)
    // Raw setter, not setUseEmailAsKey — a full reset already clears the
    // mappings, so the mapping-clear branch is redundant here.
    setUseEmailAsKeyState(false)
    setColumnMappings({})
    setColumnMatchConfidence({})
    setColumnModelConfidence({})
    setIsMatchingAI(false)
    setImportStatus('idle')
    onImportStatusChange?.('idle')
    setCreatedCount(0)
    setUpdatedCount(0)
    setFailedCount(0)
    setFailedImportRowValues([])
    setImportDuration(null)
    onClose()
  }

  const runImport = async (
    validRows: string[][],
    mappings: Record<number, string>,
  ) => {
    setImportStatus('running')
    onImportStatusChange?.('running')
    setCreatedCount(0)
    setUpdatedCount(0)
    setFailedCount(0)
    setFailedImportRowValues([])
    setImportDuration(null)
    const startTime = Date.now()

    const idEntry = Object.entries(mappings).find(([, v]) => v === ID_ATTR_KEY)
    const idRawIndex = idEntry !== undefined ? Number(idEntry[0]) : null

    const emailEntry = Object.entries(mappings).find(
      ([, v]) => v === EMAIL_ATTR_KEY,
    )
    const emailRawIndex =
      emailEntry !== undefined ? Number(emailEntry[0]) : null
    const matchByEmail = useEmailAsKey && emailRawIndex !== null

    const buildAttrs = (row: string[]): Record<string, unknown> => {
      const attrs: Record<string, unknown> = {}
      Object.entries(mappings).forEach(([rawIdxStr, attrKey]) => {
        if (attrKey && attrKey !== ID_ATTR_KEY) {
          const val = (row[Number(rawIdxStr)] ?? '').trim()
          if (!val) return
          const entry = enumLookups[attrKey]
          if (entry) {
            if (entry.type === 'enumarray') {
              attrs[attrKey] =
                resolveEnumArray(entry.map, val) ??
                val.split(',').map((s) => s.trim())
            } else {
              attrs[attrKey] = resolveEnum(entry.map, val) ?? val
            }
          } else {
            attrs[attrKey] = val
          }
        }
      })
      return attrs
    }

    const linkToList = async (
      contactId: string,
      { checkExisting }: { checkExisting: boolean },
    ): Promise<void> => {
      if (!listId) return
      // An updated (pre-existing) contact may already be linked to the list, so
      // guard against duplicate relations. A freshly created contact never is,
      // so we skip the extra query on that path.
      if (checkExisting) {
        const existingLinks = await load(() =>
          ContactListRelation.all()
            .transform({ filters: { list: listId, contact: contactId } })
            .count(),
        )
        if (existingLinks && existingLinks > 0) return
      }
      await ContactListRelation.create({
        list: listId,
        contact: contactId,
        contactEntity: entityName,
      })
    }

    type RowOutcome = 'created' | 'updated'

    // email is NOT a unique key: 0, 1, or many records may match. `.count()`
    // gives the true total for the "Found N" message; we only `.take(1)` when
    // there is exactly one to update. This is an exact-match filter on the
    // `email` attribute (assumed in preflight 1.1 — which was skipped — and
    // validated in QA 4.1), NOT a fuzzy search, so a partial value never
    // matches the wrong record.
    const findByEmail = async (
      email: string,
    ): Promise<{ count: number; itemId: string | null }> => {
      const count =
        (await load(() =>
          importDataClass
            .all()
            .transform({ filters: { [EMAIL_ATTR_KEY]: email } })
            .count(),
        )) ?? 0
      if (count !== 1) {
        return { count, itemId: null }
      }
      const items = await load(() =>
        importDataClass
          .all()
          .transform({ filters: { [EMAIL_ATTR_KEY]: email }, limit: 1 })
          .take(),
      )
      return { count, itemId: items?.[0]?.id() ?? null }
    }

    const importRow = async (row: string[]): Promise<RowOutcome> => {
      // Email match key takes priority when on and the row carries an email.
      // 0 matches → create (fall through); exactly 1 → link + update; >1 →
      // throw so the row is counted as failed with the "Found N" reason.
      if (matchByEmail && emailRawIndex !== null) {
        const email = (row[emailRawIndex] ?? '').trim()
        if (email) {
          const { count, itemId } = await findByEmail(email)
          if (count > 1) {
            throw new RowImportError({ code: 'ambiguousEmail', count })
          }
          if (count === 1) {
            // Scrivito has no atomic count+take, so the matched record is
            // re-read here. If it vanished between the two reads (a concurrent
            // delete), fail the row rather than fall through and create a stray
            // record the count said should not exist.
            const existingItem = itemId
              ? await load(() => importDataClass.get(itemId))
              : null
            if (!existingItem) {
              throw new RowImportError({ code: 'recordChanged' })
            }
            // Link before update — a link failure then leaves the contact
            // untouched, so a failed row never had its data mutated.
            await linkToList(existingItem.id(), { checkExisting: true })
            await existingItem.update(buildAttrs(row))
            return 'updated'
          }
        }
      }

      const idValue = idRawIndex !== null ? (row[idRawIndex] ?? '').trim() : ''

      if (idValue) {
        // `get` returns null for a missing record; a rejection here means an
        // operational failure (network/auth). Let it propagate so the row is
        // counted as failed rather than silently creating a duplicate.
        const existingItem = await load(() => importDataClass.get(idValue))
        if (existingItem) {
          // Link before update: a link failure then leaves the contact
          // untouched, so a row reported as failed never had its data mutated.
          // Linking is idempotent (checkExisting), so retrying is safe.
          await linkToList(existingItem.id(), { checkExisting: true })
          await existingItem.update(buildAttrs(row))
          return 'updated'
        }
      }

      const newItem = await importDataClass.create(buildAttrs(row))
      await linkToList(newItem.id(), { checkExisting: false })
      return 'created'
    }

    const localFailed: FailedImportRow[] = []

    for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
      const batch = validRows.slice(i, i + BATCH_SIZE)
      const results = await Promise.allSettled(
        batch.map((row) => importRow(row)),
      )

      let batchCreated = 0
      let batchUpdated = 0
      let batchFailed = 0
      results.forEach((result, batchIdx) => {
        if (result.status === 'fulfilled') {
          if (result.value === 'updated') batchUpdated++
          else batchCreated++
        } else {
          batchFailed++
          // Business failures carry a structured reason; anything else
          // (network/auth) surfaces as a generic, still-translated "unknown".
          const reason: FailedReason =
            result.reason instanceof RowImportError
              ? result.reason.failedReason
              : { code: 'unknown' }
          localFailed.push({ values: batch[batchIdx]!, reason })
        }
      })

      setCreatedCount((prev) => prev + batchCreated)
      setUpdatedCount((prev) => prev + batchUpdated)
      setFailedCount((prev) => prev + batchFailed)

      if (i + BATCH_SIZE < validRows.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS))
      }
    }

    setFailedImportRowValues(localFailed)
    setImportDuration(Date.now() - startTime)
    setImportStatus('done')
    onImportStatusChange?.('done')
  }

  const handleNext = () => {
    if (step === 2) {
      const dataRows = useFirstRowAsHeaders ? csvRows.slice(1) : csvRows
      const issueSet = new Set(
        csvAnalysis.issues.map((issue) => issue.rowIndex),
      )
      const validRows = dataRows.filter((_, i) => !issueSet.has(i))
      setStep(3)
      void runImport(validRows, columnMappings)
      return
    }
    setStep((s) => s + 1)
  }

  const handleBack = () => {
    setStep((s) => s - 1)
  }

  const handleMappingChange = (index: number, value: string) => {
    setColumnMappings((prev) => ({ ...prev, [index]: value }))
    // A manual override is no longer an auto-match — drop both the match-tier
    // badge and the AI confidence colour so no chip is shown (= user-mapped).
    setColumnMatchConfidence((prev) => {
      if (prev[index] === undefined) {
        return prev
      }
      const next = { ...prev }
      delete next[index]
      return next
    })
    setColumnModelConfidence((prev) => {
      if (prev[index] === undefined) {
        return prev
      }
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  // Turning email-key on drops any stale `_id` mapping — once 3.1 removes `_id`
  // from the dropdown targets it is no longer a selectable option, so a mapping
  // left pointing at it would be orphaned.
  const setUseEmailAsKey = useCallback((next: boolean) => {
    setUseEmailAsKeyState(next)
    if (!next) return
    setColumnMappings((prev) => {
      const entry = Object.entries(prev).find(([, v]) => v === ID_ATTR_KEY)
      if (!entry) return prev
      const copy = { ...prev }
      delete copy[Number(entry[0])]
      return copy
    })
  }, [])

  // The email-key toggle only makes sense when the entity schema exposes an
  // `email` attribute.
  const hasEmailTarget = useMemo(
    () => schemaAttributes?.[EMAIL_ATTR_KEY] !== undefined,
    [schemaAttributes],
  )
  const isEmailMapped = useMemo(
    () => Object.values(columnMappings).includes(EMAIL_ATTR_KEY),
    [columnMappings],
  )

  const analysisBanner = useMemo(() => {
    const mappedValues = Object.values(columnMappings).filter(Boolean)
    const mappedAttributeKeys = new Set(mappedValues)
    const unmappedMandatoryKeys = [...mandatoryAttributes].filter(
      (key) => !mappedAttributeKeys.has(key),
    )
    return getAnalysisBanner({
      matchByEmail: useEmailAsKey && isEmailMapped,
      isIdMapped: mappedValues.includes(ID_ATTR_KEY),
      unmappedMandatoryLabels: unmappedMandatoryKeys,
      context,
    })
  }, [
    columnMappings,
    mandatoryAttributes,
    context,
    useEmailAsKey,
    isEmailMapped,
  ])

  const dataRowCount = useMemo(
    () => (useFirstRowAsHeaders ? csvRows.length - 1 : csvRows.length),
    [csvRows, useFirstRowAsHeaders],
  )
  const rowLimitExceeded = dataRowCount > CSV_MAX_ROWS

  const hasAnalysisError = analysisBanner.kind === 'error'
  const isNextDisabled =
    // Block advancing past file-select until matching finishes — the mappings
    // it produces are the whole point of the Map Fields step.
    (step === 0 && (!file || rowLimitExceeded || isMatchingAI)) ||
    // Email-key needs an email column to match on before Map Fields can advance.
    (step === 1 && useEmailAsKey && !isEmailMapped) ||
    (step === 2 && hasAnalysisError)

  return {
    step,
    file,
    csvColumns,
    csvAnalysis,
    columnMappings,
    columnMatchConfidence,
    columnModelConfidence,
    isMatchingAI,
    useFirstRowAsHeaders,
    setUseFirstRowAsHeaders,
    useEmailAsKey,
    setUseEmailAsKey,
    hasEmailTarget,
    isEmailMapped,
    stepTitles,
    importStatus,
    createdCount,
    updatedCount,
    failedCount,
    failedImportRowValues,
    importDuration,
    totalToImport: csvAnalysis.validCount,
    analysisBanner,
    isNextDisabled,
    rowLimitExceeded,
    isImportStep: step === CSV_STEP_COUNT - 1,
    handleFileSelect,
    handleNext,
    handleBack,
    handleClose,
    handleMappingChange,
    runBatchDiagnostic, // dev diagnostic — gated by matchingConfig.batchDiagnosticEnabled
  }
}
